/**
 * The colour picker.
 *
 * Built to the interaction rules in docs/decisions/interaction-and-devices.md:
 * pointer events throughout so mouse, touch and pen are one code path; 44px
 * targets; nothing hover-only; and no gesture without a visible equivalent.
 *
 * Three decisions worth stating:
 *
 *   **Hue lives in this module's state, not in the colour.** Grey has no hue —
 *   `rgbToHsv` correctly reports 0 for it — so deriving the hue slider from the
 *   current colour makes the slider jump to red the moment someone drags to the
 *   left edge of the square. Every picker that feels broken has this bug.
 *
 *   **Recents matter more than the square.** Clothing work is repetitive, and
 *   re-picking the same navy forty times is the actual friction. They persist,
 *   because a refresh losing them recreates exactly that friction.
 *
 *   **BrickColors are a first-class section, not an afterthought.** Designers
 *   match garments to named Roblox colours constantly and no general editor has
 *   them.
 */
import { BRICKCOLOR_GROUPS, brickColorName } from "../../shared/brickcolors";
import { type Hsv, hexToHsv, hsvToHex, parseHex, readableTextOn, toHex } from "../../shared/colour";
import { attachPopover } from "./popover";

const RECENTS_KEY = "rcm.colour.recents";
const MAX_RECENTS = 12;

function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(list)) return [];
    // Validated on read, not trusted: localStorage is user-editable and a bad
    // value here would render as an empty swatch that paints nothing.
    return list.filter((v): v is string => typeof v === "string" && parseHex(v) !== null);
  } catch {
    // Private mode, quota, or corrupt JSON. Recents are a convenience.
    return [];
  }
}

function saveRecents(list: string[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {
    // Ignore — see above.
  }
}

export interface ColourPickerOptions {
  /** The swatch button the popover hangs from. */
  anchor: HTMLElement;
  initial?: string;
  onChange: (hex: string) => void;
  /**
   * Fires when the popover closes, however it closed.
   *
   * `onChange` streams throughout a drag, so a caller that treats every one of
   * them as an edit records a hundred of them. This is the settle: capture
   * before the picker opens, commit once here. attachPopover has always
   * reported it; the picker simply never passed it on.
   */
  onClose?: () => void;
}

export function createColourPicker(opts: ColourPickerOptions) {
  let hex = opts.initial ?? "#0074BD";
  let hsv: Hsv = hexToHsv(hex) ?? { h: 205, s: 1, v: 0.74 };
  let recents = loadRecents();

  const root = document.createElement("div");
  // `popover` drives visibility from [data-open]; `floating` is the design
  // system's only shadow-bearing class and shared/tokens.test.ts fails the
  // build if any other selector carries one.
  root.className = "cp popover floating";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "Colour");

  const square = document.createElement("div");
  square.className = "cp-square";
  square.tabIndex = 0;
  square.setAttribute("role", "slider");
  square.setAttribute("aria-label", "Saturation and brightness");
  const squareDot = document.createElement("i");
  square.appendChild(squareDot);

  const hue = document.createElement("div");
  hue.className = "cp-hue";
  hue.tabIndex = 0;
  hue.setAttribute("role", "slider");
  hue.setAttribute("aria-label", "Hue");
  hue.setAttribute("aria-valuemin", "0");
  hue.setAttribute("aria-valuemax", "360");
  const hueDot = document.createElement("i");
  hue.appendChild(hueDot);

  const field = document.createElement("input");
  field.className = "cp-hex";
  field.type = "text";
  // Not `inputmode="numeric"`: a hex code contains letters, and a numeric
  // keypad on a phone cannot type them.
  field.inputMode = "text";
  field.autocapitalize = "characters";
  field.spellcheck = false;
  field.setAttribute("aria-label", "Hex colour");

  const nameEl = document.createElement("span");
  nameEl.className = "cp-name";

  const recentsRow = document.createElement("div");
  recentsRow.className = "cp-swatches";

  const recentsWrap = document.createElement("div");
  recentsWrap.className = "cp-section";
  const recentsTitle = document.createElement("h3");
  recentsTitle.textContent = "Recent";
  // appendChild rather than append(): @cloudflare/workers-types is in scope
  // here and its global declarations shadow ParentNode.append, so the
  // multi-node form resolves to a Workers signature and fails to typecheck.
  recentsWrap.appendChild(recentsTitle);
  recentsWrap.appendChild(recentsRow);

  const palette = document.createElement("div");
  palette.className = "cp-palette";

  const head = document.createElement("div");
  head.className = "cp-head";
  head.appendChild(field);
  head.appendChild(nameEl);

  for (const el of [square, hue, head, recentsWrap, palette]) root.appendChild(el);
  document.body.appendChild(root);

  // ------------------------------------------------------------------ render

  function paintPalette() {
    palette.innerHTML = "";
    for (const group of BRICKCOLOR_GROUPS) {
      const section = document.createElement("div");
      section.className = "cp-section";
      const h = document.createElement("h3");
      h.textContent = group.name;
      const row = document.createElement("div");
      row.className = "cp-swatches";
      for (const c of group.colours) {
        row.appendChild(swatch(c.hex, c.name));
      }
      section.appendChild(h);
      section.appendChild(row);
      palette.appendChild(section);
    }
  }

  function swatch(value: string, label: string): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "cp-swatch";
    b.style.background = value;
    // The name is the whole point of the palette, so it must reach a screen
    // reader and a long-press tooltip, not just a hover title.
    b.title = `${label} · ${value}`;
    b.setAttribute("aria-label", `${label}, ${value}`);
    b.addEventListener("click", () => commit(value, { remember: true }));
    return b;
  }

  function paintRecents() {
    recentsRow.innerHTML = "";
    recentsWrap.hidden = recents.length === 0;
    for (const value of recents) {
      recentsRow.appendChild(swatch(value, brickColorName(value) ?? value));
    }
  }

  function paint() {
    const pure = hsvToHex({ h: hsv.h, s: 1, v: 1 });
    square.style.setProperty("--hue", pure);
    squareDot.style.left = `${hsv.s * 100}%`;
    squareDot.style.top = `${(1 - hsv.v) * 100}%`;
    squareDot.style.background = hex;
    hueDot.style.top = `${(hsv.h / 360) * 100}%`;

    if (document.activeElement !== field) field.value = hex;
    const rgb = parseHex(hex);
    field.style.background = hex;
    field.style.color = rgb ? readableTextOn(rgb) : "inherit";

    const known = brickColorName(hex);
    nameEl.textContent = known ?? "";
    nameEl.hidden = !known;

    opts.anchor.style.setProperty("--swatch", hex);
    opts.anchor.setAttribute("aria-label", `Colour: ${known ?? hex}`);
  }

  /** Adopt a colour. `remember` is false while dragging — one recent per pick. */
  function commit(next: string, { remember = false } = {}) {
    const rgb = parseHex(next);
    if (!rgb) return;
    hex = toHex(rgb);
    const asHsv = hexToHsv(hex);
    if (asHsv) {
      // Keep the existing hue when the new colour has none, so picking black
      // does not silently reset the hue slider to red.
      hsv = { h: asHsv.s === 0 ? hsv.h : asHsv.h, s: asHsv.s, v: asHsv.v };
    }
    if (remember) {
      recents = [hex, ...recents.filter((c) => c !== hex)].slice(0, MAX_RECENTS);
      saveRecents(recents);
      paintRecents();
    }
    paint();
    opts.onChange(hex);
  }

  function fromHsv(next: Hsv, remember = false) {
    hsv = next;
    hex = hsvToHex(next);
    if (remember) {
      recents = [hex, ...recents.filter((c) => c !== hex)].slice(0, MAX_RECENTS);
      saveRecents(recents);
      paintRecents();
    }
    paint();
    opts.onChange(hex);
  }

  // ----------------------------------------------------------------- dragging

  /**
   * One drag implementation for both controls.
   *
   * `setPointerCapture` is what makes a drag survive leaving the element, which
   * matters most at the edges — the pure-white corner and the 0° end of the hue
   * strip are exactly where a finger overshoots.
   */
  function draggable(el: HTMLElement, onMove: (fx: number, fy: number) => void) {
    let dragging = false;
    const apply = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      onMove(
        Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
        Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
      );
    };
    el.addEventListener("pointerdown", (e) => {
      dragging = true;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // Non-fatal; the drag just ends if the pointer leaves.
      }
      apply(e);
      e.preventDefault();
    });
    el.addEventListener("pointermove", (e) => {
      if (dragging) apply(e);
    });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      // Remember on release, not on every move — otherwise one drag fills the
      // recents row with forty near-identical colours.
      fromHsv(hsv, true);
    };
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
  }

  draggable(square, (fx, fy) => fromHsv({ h: hsv.h, s: fx, v: 1 - fy }));
  draggable(hue, (_fx, fy) => fromHsv({ ...hsv, h: fy * 360 }));

  // Keyboard, because a slider that only responds to a pointer is not a slider.
  square.addEventListener("keydown", (e) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const map: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const d = map[e.key];
    if (!d) return;
    e.preventDefault();
    fromHsv({
      h: hsv.h,
      s: Math.min(1, Math.max(0, hsv.s + d[0])),
      v: Math.min(1, Math.max(0, hsv.v + d[1])),
    });
  });
  hue.addEventListener("keydown", (e) => {
    const step = e.shiftKey ? 30 : 5;
    if (e.key === "ArrowUp") fromHsv({ ...hsv, h: (hsv.h - step + 360) % 360 });
    else if (e.key === "ArrowDown") fromHsv({ ...hsv, h: (hsv.h + step) % 360 });
    else return;
    e.preventDefault();
  });

  // --------------------------------------------------------------------- hex

  field.addEventListener("input", () => {
    // Live while it parses, so typing a known code previews as you go — but
    // never rewrite the field mid-type, which fights the cursor.
    if (parseHex(field.value)) commit(field.value);
  });
  field.addEventListener("change", () => {
    if (parseHex(field.value)) commit(field.value, { remember: true });
    else field.value = hex; // reject rather than guess
  });

  // ------------------------------------------------------------------ opening

  function place() {
    const r = opts.anchor.getBoundingClientRect();
    // Already displayed by the time this runs — attachPopover sets data-open
    // first precisely so there is a box here to measure.
    const own = root.getBoundingClientRect();
    // Prefer to the right of the rail; flip inside the viewport when there is
    // no room, which on a phone in portrait is always.
    const left = Math.min(r.right + 8, window.innerWidth - own.width - 8);
    const top = Math.min(Math.max(8, r.top), window.innerHeight - own.height - 8);
    root.style.left = `${Math.max(8, left)}px`;
    root.style.top = `${Math.max(8, top)}px`;
  }

  const popover = attachPopover({
    anchor: opts.anchor,
    panel: root,
    place,
    onOpen: () => {
      field.value = hex;
    },
    onClose: () => opts.onClose?.(),
  });

  const api = {
    open: () => popover.open(),
    close: () => popover.close(),
    toggle: () => popover.toggle(),
    get isOpen() {
      return popover.isOpen;
    },
    get colour() {
      return hex;
    },
    setColour(next: string) {
      commit(next);
    },
    destroy() {
      popover.destroy();
      root.remove();
    },
  };

  paintPalette();
  paintRecents();
  paint();

  return api;
}

export type ColourPicker = ReturnType<typeof createColourPicker>;
